import axios from 'axios'
import BaseServerUrl from '@/assets/BaseServerUrl'
import eventBus from '@/utils/eventBus'

/**
 * 认证服务模块
 * 处理用户认证、本地缓存验证、云端同步等功能
 */
class AuthService {
  constructor() {
    this.baseUrl = BaseServerUrl
    this.tokenKey = 'userToken'
    this.userInfoKey = 'userInfo'
    this.lastLoginKey = 'lastLoginTime'
    this.loginTimeoutDays = 7 // 7天未登录自动退出
  }

  /**
   * 获取本地存储的用户信息
   */
  getLocalUserInfo() {
    try {
      const userInfo = localStorage.getItem(this.userInfoKey)
      return userInfo ? JSON.parse(userInfo) : null
    } catch (error) {
      console.error('获取本地用户信息失败:', error)
      return null
    }
  }

  /**
   * 获取本地存储的token
   */
  getLocalToken() {
    return localStorage.getItem(this.tokenKey) || localStorage.getItem('token')
  }

  getToken() {
    return this.getLocalToken()
  }

  /**
   * 获取最后登录时间
   */
  getLastLoginTime() {
    const lastLogin = localStorage.getItem(this.lastLoginKey)
    return lastLogin ? new Date(lastLogin) : null
  }

  /**
   * 设置最后登录时间
   */
  setLastLoginTime() {
    localStorage.setItem(this.lastLoginKey, new Date().toISOString())
  }

  /**
   * 检查登录是否超时（7天未登录）
   */
  isLoginExpired() {
    const lastLogin = this.getLastLoginTime()
    if (!lastLogin) return true

    const now = new Date()
    const diffTime = Math.abs(now - lastLogin)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    return diffDays > this.loginTimeoutDays
  }

  /**
   * 清除本地认证信息
   */
  clearLocalAuth() {
    localStorage.removeItem(this.tokenKey)
    localStorage.removeItem(this.userInfoKey)
    localStorage.removeItem(this.lastLoginKey)
    localStorage.removeItem('isLoggedIn')
    // 通知全局：用户认证已清除（登出/token失效），便于即时重置用户态（如自定义壁纸、头部登录按钮）
    try { eventBus.emit('auth-cleared') } catch (e) { void e }
  }

  /**
   * 保存用户信息到本地
   */
  saveUserInfo(userInfo, token) {
    localStorage.setItem(this.userInfoKey, JSON.stringify(userInfo))
    localStorage.setItem(this.tokenKey, token)
    this.setLastLoginTime()
  }

  /**
   * 验证本地token是否有效
   */
  async validateLocalToken() {
    const token = this.getLocalToken()
    if (!token) return false

    try {
      const response = await axios.post(`${this.baseUrl}/api/auth/validate-token`, {
        token: token
      })
      return response.data.success
    } catch (error) {
      const status = error?.response?.status
      if (status === 403) {
        return true
      }
      console.error('Token验证失败:', error)
      return false
    }
  }

  /**
   * 从云端获取用户信息
   */
  async getCloudUserInfo(username) {
    try {
      const response = await axios.post(`${this.baseUrl}/api/auth/get-user-info`, {
        username: username
      })
      return response.data.success ? response.data.data : null
    } catch (error) {
      console.error('获取云端用户信息失败:', error)
      return null
    }
  }

  /**
   * 验证本地账号与云端账号是否一致
   */
  async verifyAccountConsistency(localUserInfo) {
    if (!localUserInfo || !localUserInfo.username) {
      return { isValid: false, reason: 'localInfoMissing' }
    }

    try {
      const cloudUserInfo = await this.getCloudUserInfo(localUserInfo.username)

      if (!cloudUserInfo) {
        return { isValid: false, reason: 'cloudUserNotFound' }
      }

      const isConsistent = (
        localUserInfo.username === cloudUserInfo.username
      )

      if (!isConsistent) {
        return { isValid: false, reason: 'accountMismatch' }
      }

      return {
        isValid: true,
        cloudUserInfo: cloudUserInfo
      }
    } catch (error) {
      const status = error?.response?.status
      if (status === 403) {
        return {
          isValid: true,
          cloudUserInfo: localUserInfo,
          banned: true
        }
      }
      console.error('账号一致性验证失败:', error)
      return { isValid: false, reason: 'verificationError' }
    }
  }

  /**
   * 同步云端数据到本地
   */
  syncCloudDataToLocal(cloudUserInfo) {
    try {
      // 获取本地用户信息和token
      const localUserInfo = this.getLocalUserInfo()
      const token = this.getLocalToken()

      // 合并数据：保留本地的头像信息，使用云端的其他信息
      const mergedUserInfo = {
        ...cloudUserInfo,
        // 如果本地有头像且与云端不同，保留本地头像
        avatar: localUserInfo?.avatar || cloudUserInfo.avatar
      }

      this.saveUserInfo(mergedUserInfo, token)

      console.log('云端数据同步成功，已保留本地头像')
      return true
    } catch (error) {
      console.error('云端数据同步失败:', error)
      return false
    }
  }

  /**
   * 自动验证和同步流程
   * 在应用启动时调用
   */
  async autoVerifyAndSync() {
    try {
      // 1. 检查是否有本地缓存
      const localUserInfo = this.getLocalUserInfo()
      const localToken = this.getLocalToken()

      if (!localUserInfo || !localToken) {
        console.log('无本地缓存，跳过自动验证')
        return { success: true, action: 'noLocalCache' }
      }

      // 2. 检查登录是否超时
      if (this.isLoginExpired()) {
        console.log('登录已超时，自动退出')
        this.clearLocalAuth()
        return { success: true, action: 'loginExpired' }
      }

      // 3. 验证token是否有效
      const isTokenValid = await this.validateLocalToken()
      if (!isTokenValid) {
        console.log('Token无效，自动退出')
        this.clearLocalAuth()
        return { success: true, action: 'tokenInvalid' }
      }

      // 4. 验证账号一致性
      const verifyResult = await this.verifyAccountConsistency(localUserInfo)
      if (!verifyResult.isValid) {
        console.log('账号不一致，自动退出:', verifyResult.reason)
        this.clearLocalAuth()
        return { success: true, action: 'accountMismatch', reason: verifyResult.reason }
      }

      // 5. 同步云端数据到本地
      const syncSuccess = this.syncCloudDataToLocal(verifyResult.cloudUserInfo)
      if (syncSuccess) {
        // 更新最后登录时间
        this.setLastLoginTime()
        console.log('自动验证和同步完成')
        return { success: true, action: 'syncCompleted', userInfo: verifyResult.cloudUserInfo }
      } else {
        console.log('数据同步失败')
        return { success: false, action: 'syncFailed' }
      }

    } catch (error) {
      console.error('自动验证和同步过程出错:', error)
      return { success: false, action: 'error', error: error.message }
    }
  }

  /**
   * 检查用户是否已登录
   */
  isLoggedIn() {
    const userInfo = this.getLocalUserInfo()
    const token = this.getLocalToken()
    return !!(userInfo && token && !this.isLoginExpired())
  }

  /**
   * 获取当前用户信息
   */
  getCurrentUser() {
    if (this.isLoggedIn()) {
      return this.getLocalUserInfo()
    }
    return null
  }
}

// 创建单例实例
const authService = new AuthService()

export default authService